/** @jsx isolateComponent */

import React, { useEffect, useRef, useState } from 'react';
import { css } from '@emotion/react';
import type { Ref } from 'react';

import { Link } from '../../navigation/Link';
import { Colors, Typography } from '../../../theme';
import {
  isolateComponent,
  labelStyles,
  withModifier,
} from '@zapier/style-encapsulation';

import { TabsDropdown } from './TabsDropdown';

import type { TabsItemType, TabsSize, OnChooseItem } from './types';

export type TabsRefElement = HTMLDivElement;

export type Props = {
  /**
   * The `aria-current` value to apply to the selected item.
   */
  ariaCurrent?: 'page' | 'step';
  /**
   * Optional label that provides context as to what the navigation is used for.
   * Used by screen reading software.
   */
  ariaLabel?: string;
  /**
   * React ref that is forwarded along as a prop to facilitate
   * typing and to eliminate use of `forwardRef`.
   */
  forwardedRef?: Ref<TabsRefElement>;
  /**
   * An array of items to render within the navigation.
   */
  items: Array<TabsItemType>;
  /**
   * Callback for when an item is chosen.
   */
  onChooseItem: OnChooseItem;
  /**
   * The `href` of the currently selected navigation item. `href` is
   * used rather than the full item since the item object could be
   * cloned, which ruins `===` checks.
   */
  selectedItemHref: string;
  /**
   * Optional value that will determine if the nav item should be highlighted
   * or not. When not provided, the `href` will be compared to the
   * `selectedItemHref` passed to `Tabs`.
   */
  isItemSelected: (item: TabsItemType, selectedItemHref: string) => boolean;
  /**
   * Size of navigation. `medium` typically used for small screens
   * and `large` typically used for larger screens.
   */
  size?: TabsSize;
  /**
   * Whether or not the component should render the gray indicator track beneath the nav items.
   */
  shouldRenderTrack: boolean;
};

const indicatorTrackHeight = 3;

const Styles = labelStyles('Tabs', {
  root: (props: Props) => [
    css`
      ${Typography.paragraph3Semibold}
      position: relative;
      display: grid;
      grid-template-columns: 1fr auto;
      column-gap: 20px;
      row-gap: 2px;
      align-items: center;
    `,

    props.size === 'medium' &&
      withModifier(
        'medium',
        css`
          height: 50px;
        `
      ),

    props.size === 'large' &&
      withModifier(
        'large',
        css`
          height: 70px;
        `
      ),
  ],

  primary: ({ count }: { count: number }) => [
    css`
      display: grid;
      height: 100%;
      // Allow shrinking
      min-width: 0;
      max-width: 100%;
    `,

    // `minmax(auto, min-content)` behaves such that at the min value of
    // `auto` items could truncate if necessary, and at the max value of
    // `min-content` items will not render any wider than they need to,
    // ensuring that they are left-aligned rather than stretching to fill
    // any leftover space.
    withModifier(
      `count-${count}`,
      css`
        grid-template-columns: repeat(${count}, minmax(auto, min-content));
      `
    ),
  ],

  item: ({ isSelected }: { isSelected: boolean }) => [
    css`
      display: flex;
      align-items: center;
      justify-content: center;
      text-align: center;
      color: ${Colors.neutral800};
      font-weight: 600;
      text-decoration: none;
      padding: 0 20px;

      &:not([disabled]):hover,
      &:not([disabled]):focus {
        color: ${Colors.blue};
        outline: none;
      }

      &[disabled] {
        color: ${Colors.neutral500};
        cursor: not-allowed;
      }
    `,
    isSelected &&
      withModifier(
        'selected',
        css`
          color: ${Colors.blue};
        `
      ),
  ],

  itemText: css`
    display: block;
    white-space: nowrap;
  `,

  indicatorTrack: (props: Props) => [
    css`
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      // This ends up being a non-integer, but it actually renders better
      // in Chrome (and still renders fine in other browsers) this way.
      border-radius: ${indicatorTrackHeight / 2}px;
      height: ${indicatorTrackHeight}px;
      overflow: hidden;
    `,
    // The track always renders to the DOM to contain the indicator,
    // but it shouldn't always _visibly_ render.
    props.shouldRenderTrack &&
      withModifier(
        'visible',
        css`
          background-color: ${Colors.neutral300};
        `
      ),
  ],

  indicator: css`
    position: absolute;
    height: inherit;
    border-radius: inherit;
    background-color: ${Colors.blue};
    transition: all 250ms ease-in-out;
  `,
});

type TabsItemRefElement = HTMLAnchorElement | HTMLDivElement;

type TabsItemProps = TabsItemType & {
  ariaCurrent?: 'page' | 'step';
  onChoose: (event: React.MouseEvent) => void;
  forwardedRef: Ref<TabsItemRefElement> | null;
  isSelected: boolean;
};

// Since this is a component rather than a DOM node, `forwardRef`
// is used to facilitate properly passing the `ref` along to the DOM node.
const TabsItem = (props: TabsItemProps) => {
  // Render a `button[disabled]` when `props.disabled` to prevent
  // users from tabbing to it while still providing accessible feedback
  // if users navigate to it in another way. Note that `onClick` and
  // `ref` are also not applied since a disabled `NavItem` shouldn't
  // be able to be clicked (`onClick`) or selected (`ref`).
  if (props.disabled) {
    return (
      <button
        type="button"
        aria-label={props.ariaLabel}
        css={Styles.item(props)}
        disabled={true}
      >
        <span css={Styles.itemText}>{props.text}</span>
      </button>
    );
  }

  return (
    <Link
      ariaLabel={props.ariaLabel}
      ariaCurrent={props.isSelected ? props.ariaCurrent : undefined}
      color={null}
      css={Styles.item(props)}
      href={props.href}
      onClick={props.onChoose}
      ref={props.forwardedRef as Ref<HTMLAnchorElement>}
    >
      <span css={Styles.itemText}>{props.text}</span>
    </Link>
  );
};

/**
 * Creates a horizontal navigation component that can display a variable
 * number of items, and allows excess items to be nested into a dropdown menu.
 */
export const Tabs = (originalProps: Props) => {
  const props = {
    ...originalProps,
    size: originalProps.size || 'large',
  };

  // Hold onto ref so the selected node can be measured
  // in order to position `indicator` via `indicatorStyles`.
  const selectedItemRef = useRef<TabsItemRefElement>(null);
  const [indicatorStyles, setIndicatorStyles] = useState({});

  const shouldRenderIndicator = !props.items.every((i) => i.isExtra);

  const primaryItems = props.items.filter((item) => !item.isExtra);
  const primaryLinks = primaryItems.map((item) => {
    const isSelected = props.isItemSelected(item, props.selectedItemHref);
    return (
      <TabsItem
        {...item}
        ariaCurrent={props.ariaCurrent}
        forwardedRef={
          isSelected && shouldRenderIndicator ? selectedItemRef : null
        }
        isSelected={isSelected}
        key={item.href}
        onChoose={(event) => props.onChooseItem(item, event)}
      />
    );
  });

  const secondaryItems = props.items.filter((item) => item.isExtra);
  const isSecondaryItemSelected = secondaryItems.some((item) => {
    return props.isItemSelected(item, props.selectedItemHref);
  });
  const secondaryMenu = secondaryItems.length > 0 && (
    <div
      ref={
        isSecondaryItemSelected && shouldRenderIndicator
          ? (selectedItemRef as Ref<HTMLDivElement>)
          : null
      }
    >
      <TabsDropdown
        size={props.size}
        items={secondaryItems}
        onChooseItem={props.onChooseItem}
        selectedItemHref={props.selectedItemHref}
      />
    </div>
  );

  // Whenever `selectedItemHref` changes, read the position of
  // the `current`ly selected node.
  useEffect(() => {
    // `requestAnimationFrame` ensures rendering has completed
    // and measuring is accurate. Without this some measurements
    // may be off on initial render.
    const requestId = requestAnimationFrame(() => {
      setIndicatorStyles(
        selectedItemRef.current
          ? {
              left: selectedItemRef.current!.offsetLeft,
              width: selectedItemRef.current!.offsetWidth,
            }
          : {}
      );
    });
    return () => cancelAnimationFrame(requestId);
  }, [props.items, props.selectedItemHref]);

  return (
    <nav aria-label={props.ariaLabel} css={Styles.root(props)}>
      <div
        css={Styles.primary({ count: primaryLinks.length })}
        ref={props.forwardedRef}
      >
        {primaryLinks}
      </div>
      {secondaryMenu}
      <div css={Styles.indicatorTrack(props)}>
        <div css={Styles.indicator} style={indicatorStyles} />
      </div>
    </nav>
  );
};

Tabs.defaultProps = {
  ariaCurrent: 'page',
  isItemSelected: (item: TabsItemType, selectedItemHref: string) =>
    item.href === selectedItemHref,
  onChooseItem: () => {},
  shouldRenderTrack: true,
};
